import { Interval, IntervalEnum } from '@/types/common.type';
import { DateTime } from 'luxon';

export const getGreeting = (): string => {
  const currentHour = DateTime.now().hour;

  if (currentHour < 12) {
    return 'Good Morning';
  }

  if (currentHour < 18) {
    return 'Good Afternoon';
  }

  return 'Good Evening';
};

export const USToISODate = (val: string): string => {
  const [month, day, year] = val.split('/');
  return `${year}-${month}-${day}`;
};

export const ISOToUSDate = (val: string): string => {
  const [year, month, day] = val.split('-');
  return `${month}/${day}/${year}`;
};

export const diffInDays = (
  date1: Date | string,
  date2: Date | string
): number => {
  const d1 = typeof date1 == 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 == 'string' ? new Date(date2) : date2;
  const diffTime = Math.abs(d2.getTime() - d1.getTime());

  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const convertIntervalToDays = ({ unit, count }: Interval): number => {
  switch (unit) {
    case IntervalEnum.DAY:
      return count;
    case IntervalEnum.WEEK:
      return count * 7;
    case IntervalEnum.MONTH:
      return count * 30;
    case IntervalEnum.YEAR:
      return count * 356;
  }
};

export const convertIntervalToMonths = (interval: Interval): number => {
  return Math.trunc(convertIntervalToDays(interval) / 30);
};

export const getIntervalDisplay = (
  { count, unit }: Interval,
  suffix?: string
): string => {
  return (
    `${count} ${unit}${count > 1 ? 's' : ''}` + (suffix ? ` ${suffix}` : '')
  );
};

export const isToday = (value: Date | string): boolean => {
  let datetime;
  if (typeof value === 'string') {
    datetime = DateTime.fromISO(value);
  } else {
    datetime = DateTime.fromJSDate(value);
  }

  return datetime.toISODate() === DateTime.local().toISODate();
};

export const isInPast = (value: Date | string): boolean => {
  let datetime;
  if (typeof value === 'string') {
    datetime = DateTime.fromISO(value);
  } else {
    datetime = DateTime.fromJSDate(value);
  }

  return datetime < DateTime.local();
};

/**
 * Parse date to 2-digit format MM/DD/YY or MM/DD
 *
 * @param value
 * @param excludeYear
 */
export const parseDate2Digits = (
  value: Date | string,
  excludeYear: boolean = false
): string => {
  let datetime;
  if (typeof value === 'string') {
    datetime = DateTime.fromISO(value);
  } else {
    datetime = DateTime.fromJSDate(value);
  }

  let formatOptions: Record<string, string> = {
    month: '2-digit',
    day: '2-digit'
  };

  if (!excludeYear) {
    formatOptions = {
      ...formatOptions,
      year: '2-digit'
    };
  }

  return datetime.toLocaleString(formatOptions);
};

/**
 * Compare intervals to determine which is larger
 * Returns positive if a > b, negative if a < b, 0 if equal
 *
 * @param a {Interval} First interval
 * @param b {Interval} Second interval
 * @return {number} Comparison result
 */
export const compareIntervals = (a: Interval, b: Interval): number => {
  // Convert both to same unit (months) for comparison
  const aInMonths = a.unit === IntervalEnum.MONTH ? a.count : a.count / 30;
  const bInMonths = b.unit === IntervalEnum.MONTH ? b.count : b.count / 30;
  return aInMonths - bInMonths;
};
